/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const padMonth = (m) => String(m).padStart(2, '0');

const initFind = {
  search: '',
  order_by: 'branch_id',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  year: new Date().getFullYear(),
  month: padMonth(new Date().getMonth() + 1),
  branch: 'ALL',
  category: 'ALL',
};

function RpCatGp() {
  const mod = 'rp_cat_gp';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [urlExcel, setUrlExcel] = useState('');
  const [cboYear, setCboYear] = useState([]);
  const [cboMonth, setCboMonth] = useState([]);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboCategory, setCboCategory] = useState([]);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'month', label: 'เดือน', align: 'center' },
    { id: 'shortcode', label: 'สาขา', align: 'left' },
    { id: 'cat_name', label: 'หมวดหมู่', align: 'left' },
    { id: 'nettotal', label: 'ยอดขาย', align: 'right' },
    { id: 'total_cost', label: 'ทุน', align: 'right' },
    { id: 'gross_profit', label: '%GP', align: 'right' },
    { id: 'stock_value', label: 'มูลค่าคงเหลือ', align: 'right' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      year: data.year,
      month: data.month,
      branch: data.branch,
      category: data.category,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('year', find.year);
    params.append('month', find.month);
    params.append('branch', find.branch);
    params.append('category', find.category);
    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setUrlExcel(result.excel);
        setCboYear(result.year || []);
        setCboMonth(result.month || []);
        setCboBranch(result.branch || []);
        setCboCategory(result.category || []);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <MDInput
              fullWidth
              select
              value={find.year}
              onChange={(e) => view({ ...find, year: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.year')} :</InputAdornment>
                ),
              }}
            >
              {cboYear.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <MDInput
              fullWidth
              select
              value={find.month}
              onChange={(e) => view({ ...find, month: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.month')} :</InputAdornment>
                ),
              }}
            >
              {cboMonth.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MDInput
              fullWidth
              select
              value={find.branch}
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.shortcode || item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MDInput
              fullWidth
              select
              value={find.category}
              onChange={(e) => view({ ...find, category: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.category')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='NONE'>ไม่มีหมวดหมู่</MenuItem>
              {cboCategory.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='info' onClick={() => view({ ...find, page: 1 })}>
                <Icon>search</Icon>&nbsp;ค้นหา
              </MDButton>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={`${row.branch_id}-${row.cat_id ?? 'none'}`}>
                      <TableCell align='center' className='nowrap'>
                        {row.month}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.shortcode}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.cat_name}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.nettotal)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.total_cost)}
                      </TableCell>
                      <TableCell align='right' className='nowrap color-success'>
                        {Func.numberDisplay(row.gross_profit, 2)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.stock_value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_account`), t(`menu.rp_cat_gp`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpCatGp;
